import { Venue, VenueTableQrSection, VenueTableQrTable } from 'shared/types/api'
import { findTableQrTable, formatTableQrTable } from 'shared/table-qr'
import { getApiHost } from '@/utils/fetch'

// QR-код на каждый стол. Ссылка стола: /venue/<name>?om=onSite&table=<номер>&hall=<id зала>.
// Гость, пришедший по такой ссылке, не вводит номер стола при заказе «в зале».
//
// Привязка к столу живёт 4 часа (один визит). Ссылка со столом остаётся в истории браузера,
// поэтому стол из адресной строки убираем сразу после сканирования, а сохранённый стол
// по истечении срока забываем: на следующий день старая вкладка не закажет «не на тот стол».

export const TABLE_QR_URL_PARAM = 'table'
export const TABLE_QR_HALL_URL_PARAM = 'hall'
const TABLE_QR_TTL_MS = 4 * 60 * 60 * 1000

type VenueLike = Pick<Venue, 'name' | 'features'>
type TableRef = { number: string; hall?: string }
type StoredTable = TableRef & { at: number }

const storageKey = (venueName: string) => {
  return `foodeon/tableQr/${venueName}`
}

const isValidPart = (value: string) => {
  return value.length > 0 && value.length <= 20 && /^[\p{L}\p{N} _.\-№#]+$/u.test(value)
}

const readUrlTable = (): TableRef | null => {
  try {
    const params = new URL(window.location.href).searchParams
    const number = params.get(TABLE_QR_URL_PARAM)?.trim()
    const hall = params.get(TABLE_QR_HALL_URL_PARAM)?.trim() || undefined
    if (!number || !isValidPart(number) || (hall && !isValidPart(hall))) {
      return null
    }
    return { number, hall }
  } catch {
    return null
  }
}

const clearStored = (venueName: string) => {
  try {
    localStorage.removeItem(storageKey(venueName))
    sessionStorage.removeItem(storageKey(venueName))
  } catch {
    // нечего чистить
  }
}

const writeStored = (venueName: string, ref: TableRef) => {
  const value = JSON.stringify({ ...ref, at: Date.now() } as StoredTable)
  try {
    localStorage.setItem(storageKey(venueName), value)
  } catch {
    try {
      sessionStorage.setItem(storageKey(venueName), value)
    } catch {
      // storage недоступен — стол живёт, только пока он в адресной строке
    }
  }
}

const readStored = (venueName: string): TableRef | null => {
  const raw = (() => {
    try {
      return (
        localStorage.getItem(storageKey(venueName)) ?? sessionStorage.getItem(storageKey(venueName))
      )
    } catch {
      return null
    }
  })()
  if (!raw) {
    return null
  }
  try {
    const stored = JSON.parse(raw) as StoredTable
    if (
      !stored?.number ||
      typeof stored.at !== 'number' ||
      Date.now() - stored.at > TABLE_QR_TTL_MS
    ) {
      clearStored(venueName)
      return null
    }
    return { number: stored.number, hall: stored.hall }
  } catch {
    clearStored(venueName)
    return null
  }
}

// убрать стол из адресной строки (и из истории браузера)
const removeTableFromUrl = () => {
  try {
    const url = new URL(window.location.href)
    if (
      !url.searchParams.has(TABLE_QR_URL_PARAM) &&
      !url.searchParams.has(TABLE_QR_HALL_URL_PARAM)
    ) {
      return
    }
    url.searchParams.delete(TABLE_QR_URL_PARAM)
    url.searchParams.delete(TABLE_QR_HALL_URL_PARAM)
    window.history.replaceState(window.history.state, '', url.toString())
  } catch {
    // не получилось — не страшно, срок привязки всё равно ограничен
  }
}

// «QR-коды столов» нужны, только если в зале принимают заказы и спрашивают номер стола
// (в заведениях без официантов номер стола не нужен — и QR столов тоже)
export function isTableQrAvailable(venue: VenueLike) {
  return !!venue.features.orderingTypes?.onSite?.useTableNumber
}

export function isTableQrActive(venue: VenueLike) {
  return !!venue.features.tableQr?.enabled && isTableQrAvailable(venue)
}

const resolve = (venue: VenueLike, ref: TableRef | null) => {
  return ref ? findTableQrTable(venue.features.tableQr, ref.number, ref.hall) : undefined
}

// вызывать на странице меню: если пришли по QR стола — запомнить стол и убрать его из URL
// Возвращает адрес без стола, который нужно поставить в адресную строку (через router.replace —
// history.replaceState Next.js перетирает при гидрации статической страницы), или null.
export function captureTableFromQr(venue: VenueLike): string | null {
  if (typeof window === 'undefined' || !isTableQrActive(venue)) {
    // данные страницы могли ещё не обновиться после включения — ссылку не трогаем,
    // со следующей загрузки стол подхватится
    return null
  }
  const fromUrl = readUrlTable()
  if (fromUrl && resolve(venue, fromUrl)) {
    writeStored(venue.name, fromUrl)
  }
  return urlWithoutTable()
}

// текущий адрес без параметров стола; null — их там нет
function urlWithoutTable(): string | null {
  try {
    const url = new URL(window.location.href)
    if (
      !url.searchParams.has(TABLE_QR_URL_PARAM) &&
      !url.searchParams.has(TABLE_QR_HALL_URL_PARAM)
    ) {
      return null
    }
    url.searchParams.delete(TABLE_QR_URL_PARAM)
    url.searchParams.delete(TABLE_QR_HALL_URL_PARAM)
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}

// стол, по QR которого пришёл гость (не старше 4 часов); null — QR столов выключены,
// гость пришёл не по QR стола, стола больше нет в настройках или привязка устарела
export function getTableFromQr(venue: VenueLike): VenueTableQrTable | null {
  if (typeof window === 'undefined' || !isTableQrActive(venue)) {
    return null
  }
  return resolve(venue, readUrlTable()) ?? resolve(venue, readStored(venue.name)) ?? null
}

// «Изменить» в корзине: гость пересел — забываем стол из QR, номер спросим вручную
export function clearTableFromQr(venue: VenueLike) {
  if (typeof window === 'undefined') {
    return
  }
  clearStored(venue.name)
  removeTableFromUrl()
}

// выбор стола в корзине (гость пришёл не по QR или нажал «Изменить»): залы и столы —
// только те, что заведены в панели. Пусто — QR столов выключены или столов нет (тогда — обычное поле)
export type TableQrPickerGroup = { section?: VenueTableQrSection; tables: VenueTableQrTable[] }

export function getTableQrPickerGroups(venue: VenueLike): TableQrPickerGroup[] {
  const tableQr = venue.features.tableQr
  if (!tableQr || !isTableQrActive(venue)) {
    return []
  }
  const byNumber = (a: VenueTableQrTable, b: VenueTableQrTable) => {
    return a.number.localeCompare(b.number, 'ru', { numeric: true })
  }
  const allTables = tableQr.tables ?? []
  const sectionIds = (tableQr.sections ?? []).map((section) => {
    return section.id
  })
  return [
    ...(tableQr.sections ?? []).map((section) => {
      return {
        section,
        tables: allTables
          .filter((table) => {
            return table.sectionId === section.id
          })
          .sort(byNumber),
      }
    }),
    {
      section: undefined,
      tables: allTables
        .filter((table) => {
          return !table.sectionId || !sectionIds.includes(table.sectionId)
        })
        .sort(byNumber),
    },
  ].filter((group) => {
    return group.tables.length > 0
  })
}

export function formatTableFromQr(venue: VenueLike, table: VenueTableQrTable, language?: string) {
  return formatTableQrTable(venue.features.tableQr, table, language)
}

export function makeTableQrUrl(
  origin: string,
  venueName: string,
  table: Pick<VenueTableQrTable, 'number' | 'sectionId'>,
) {
  const url = new URL(`/venue/${venueName}`, origin)
  url.searchParams.set('om', 'onSite')
  url.searchParams.set(TABLE_QR_URL_PARAM, table.number)
  if (table.sectionId) {
    url.searchParams.set(TABLE_QR_HALL_URL_PARAM, table.sectionId)
  }
  url.searchParams.set('utm_source', 'table_qr')
  url.searchParams.set('utm_medium', 'qr_code')
  return url.toString()
}

export function makeTableQrSvgUrl(tableUrl: string) {
  return `${getApiHost()}/qr-code.svg?data=${encodeURIComponent(tableUrl)}`
}
